import { create } from 'zustand';
import { useProfileStore } from './profileStore';
import type { ApiServices } from '../hooks/useApi';
import type {
  SubscriptionData,
  Invoice,
  InvoiceDetails,
  PreviewChangeResponse,
  CancelSubscriptionRequest,
  Product,
  PreviewAddonChangeResponse
} from '../services/api/subscription';
import type { AddonType, SubscriptionAddon } from '../types/Subscription';

interface SubscriptionState {
  // State
  currentSubscription: SubscriptionData | null;
  products: Product[];
  invoices: Invoice[];
  selectedInvoice: InvoiceDetails | null;
  hasMoreInvoices: boolean;
  prorationPreview: PreviewChangeResponse | null;

  // Loading states
  isLoadingSubscription: boolean;
  isLoadingProducts: boolean;
  isLoadingInvoices: boolean;
  isChangingPlan: boolean;
  isCanceling: boolean;
  isReactivating: boolean;
  isCreatingSession: boolean;
  isAddingAddon: boolean;
  isRemovingAddon: boolean;

  // Error state
  error: Error | null;

  // Actions
  fetchSubscription: (api: ApiServices) => Promise<void>;
  fetchProducts: (api: ApiServices) => Promise<void>;
  fetchInvoices: (api: ApiServices, limit?: number, startingAfter?: string) => Promise<void>;
  fetchInvoiceDetails: (api: ApiServices, invoiceId: string) => Promise<void>;
  createPortalSession: (api: ApiServices, returnUrl: string) => Promise<string>;
  createCheckoutSession: (api: ApiServices, params: {
    priceIds: string[];
    successUrl: string;
    cancelUrl: string;
    trialDays?: number;
  }) => Promise<string>;
  changeSubscription: (api: ApiServices, priceId: string) => Promise<void>;
  previewChange: (api: ApiServices, priceId: string) => Promise<void>;
  cancelSubscription: (api: ApiServices, params?: CancelSubscriptionRequest) => Promise<void>;
  reactivateSubscription: (api: ApiServices) => Promise<void>;
  addAddon: (api: ApiServices, addonPriceId: string) => Promise<void>;
  removeAddon: (api: ApiServices, addonType: AddonType) => Promise<void>;
  previewAddonChange: (api: ApiServices, params: { addPriceIds?: string[]; removeAddonTypes?: string[] }) => Promise<PreviewAddonChangeResponse>;

  // Computed helpers
  hasActiveSubscription: () => boolean;
  isSubscriptionCanceled: () => boolean;
  getCurrentPlan: () => { id: string; name: string; price: number; period: string } | null;
  hasAddon: (type: AddonType) => boolean;
  getActiveAddons: () => SubscriptionAddon[];
  hasTeamOutcomes: () => boolean;
  hasPrivateModels: () => boolean;

  // Trial helpers
  isInTrial: () => boolean;
  isTrialExpired: () => boolean;
  getTrialDaysRemaining: () => number;
  getTrialUrgencyLevel: () => 'normal' | 'warning' | 'urgent';

  // Utility
  setError: (error: Error | null) => void;
  reset: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  // Initial state
  currentSubscription: null,
  products: [],
  invoices: [],
  selectedInvoice: null,
  hasMoreInvoices: false,
  prorationPreview: null,
  isLoadingSubscription: false,
  isLoadingProducts: false,
  isLoadingInvoices: false,
  isChangingPlan: false,
  isCanceling: false,
  isReactivating: false,
  isCreatingSession: false,
  isAddingAddon: false,
  isRemovingAddon: false,
  error: null,

  // Actions
  fetchSubscription: async (api) => {
    set({ isLoadingSubscription: true, error: null });
    try {
      const response = await api.subscription.getCurrentSubscription();
      const subscription = response.subscription || null;
      set({
        currentSubscription: subscription,
        isLoadingSubscription: false
      });
      // Sync live Stripe data to profile store so route guards (CreatorRoute) see fresh status
      useProfileStore.getState().updateProfile({ subscription: subscription ?? undefined });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      set({
        error: error instanceof Error ? error : new Error(String(error)),
        isLoadingSubscription: false
      });
    }
  },

  fetchProducts: async (api) => {
    set({ isLoadingProducts: true, error: null });
    try {
      const response = await api.subscription.listProducts();
      set({
        products: response.products,
        isLoadingProducts: false
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      set({
        error: error instanceof Error ? error : new Error(String(error)),
        isLoadingProducts: false
      });
    }
  },

  fetchInvoices: async (api, limit = 10, startingAfter?) => {
    set({ isLoadingInvoices: true, error: null });
    try {
      const response = await api.subscription.listInvoices(limit, startingAfter);

      // If startingAfter is provided, append to existing invoices (pagination)
      const currentInvoices = startingAfter ? get().invoices : [];

      set({
        invoices: [...currentInvoices, ...response.invoices],
        hasMoreInvoices: response.hasMore,
        isLoadingInvoices: false
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
      set({
        error: error instanceof Error ? error : new Error(String(error)),
        isLoadingInvoices: false
      });
    }
  },

  fetchInvoiceDetails: async (api, invoiceId) => {
    set({ error: null });
    try {
      const invoice = await api.subscription.getInvoice(invoiceId);
      set({ selectedInvoice: invoice });
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      set({ error: error instanceof Error ? error : new Error(String(error)) });
    }
  },

  createPortalSession: async (api, returnUrl) => {
    set({ isCreatingSession: true, error: null });
    try {
      const portalUrl = await api.subscription.createPortalSession(returnUrl);
      set({ isCreatingSession: false });
      return portalUrl;
    } catch (error) {
      console.error('Error creating portal session:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({
        error: wrappedError,
        isCreatingSession: false
      });
      throw error;
    }
  },

  createCheckoutSession: async (api, params) => {
    set({ isCreatingSession: true, error: null });
    try {
      const checkoutUrl = await api.subscription.createCheckoutSession({
        ...params,
        mode: 'subscription',
        allowPromotionCodes: true
      });
      set({ isCreatingSession: false });
      return checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({
        error: wrappedError,
        isCreatingSession: false
      });
      throw error;
    }
  },

  changeSubscription: async (api, priceId) => {
    set({ isChangingPlan: true, error: null });
    try {
      const subscription = await api.subscription.changeSubscription(priceId);
      const current = get().currentSubscription;
      set({
        currentSubscription: current ? { ...current, ...subscription } : subscription,
        isChangingPlan: false,
        prorationPreview: null // Clear preview after change
      });
    } catch (error) {
      console.error('Error changing subscription:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({
        error: wrappedError,
        isChangingPlan: false
      });
      throw error;
    }
  },

  previewChange: async (api, priceId) => {
    set({ error: null });
    try {
      const preview = await api.subscription.previewSubscriptionChange(priceId);
      set({ prorationPreview: preview });
    } catch (error) {
      console.error('Error previewing change:', error);
      set({ error: error instanceof Error ? error : new Error(String(error)) });
      throw error;
    }
  },

  cancelSubscription: async (api, params = {}) => {
    set({ isCanceling: true, error: null });
    try {
      const response = await api.subscription.cancelSubscription(params);

      // Update the current subscription with cancellation info
      const current = get().currentSubscription;
      if (current) {
        set({
          currentSubscription: {
            ...current,
            status: response.subscription.status as SubscriptionData['status'],
            cancelAtPeriodEnd: response.subscription.cancelAtPeriodEnd,
            canceledAt: response.subscription.canceledAt
          },
          isCanceling: false
        });
      } else {
        set({ isCanceling: false });
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({
        error: wrappedError,
        isCanceling: false
      });
      throw error;
    }
  },

  reactivateSubscription: async (api) => {
    set({ isReactivating: true, error: null });
    try {
      const subscription = await api.subscription.reactivateSubscription();
      set({
        currentSubscription: subscription,
        isReactivating: false
      });
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({
        error: wrappedError,
        isReactivating: false
      });
      throw error;
    }
  },

  addAddon: async (api, addonPriceId) => {
    set({ isAddingAddon: true, error: null });
    try {
      const response = await api.subscription.addAddon(addonPriceId);
      const current = get().currentSubscription;
      if (current) {
        set({
          currentSubscription: {
            ...current,
            addons: response.addons,
            features: response.features,
          },
          isAddingAddon: false,
        });
      } else {
        set({ isAddingAddon: false });
      }
    } catch (error) {
      console.error('Error adding addon:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({ error: wrappedError, isAddingAddon: false });
      throw error;
    }
  },

  removeAddon: async (api, addonType) => {
    set({ isRemovingAddon: true, error: null });
    try {
      const response = await api.subscription.removeAddon(addonType);
      const current = get().currentSubscription;
      if (current) {
        set({
          currentSubscription: {
            ...current,
            addons: response.addons,
            features: response.features,
          },
          isRemovingAddon: false,
        });
      } else {
        set({ isRemovingAddon: false });
      }
    } catch (error) {
      console.error('Error removing addon:', error);
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      set({ error: wrappedError, isRemovingAddon: false });
      throw error;
    }
  },

  previewAddonChange: async (api, params) => {
    set({ error: null });
    try {
      const preview = await api.subscription.previewAddonChange(params);
      return preview;
    } catch (error) {
      console.error('Error previewing addon change:', error);
      set({ error: error instanceof Error ? error : new Error(String(error)) });
      throw error;
    }
  },

  // Computed helpers
  hasActiveSubscription: () => {
    const sub = get().currentSubscription;
    return !!sub && sub.status === 'active' && !sub.cancelAtPeriodEnd;
  },

  isSubscriptionCanceled: () => {
    const sub = get().currentSubscription;
    return !!sub && (sub.cancelAtPeriodEnd === true);
  },

  getCurrentPlan: () => {
    const sub = get().currentSubscription;
    const products = get().products;
    if (!sub || !sub.planId) return null;

    const product = products.find(p => p.id === sub.planId);
    if (!product) return null;

    // Find the current price based on subscription priceId or default to first price
    const currentPrice = product.prices.find(p => p.id === sub.priceId) || product.prices[0];
    if (!currentPrice || !currentPrice.unitAmount) return null;

    return {
      id: product.id,
      name: product.name,
      price: currentPrice.unitAmount / 100, // Convert from cents to dollars
      period: currentPrice.interval || 'month'
    };
  },

  hasAddon: (type: AddonType) => {
    const sub = get().currentSubscription;
    if (!sub?.addons) return false;
    // Bundle counts as having both player_props and private
    if (type === 'player_props') return sub.addons.some(a => a.addonType === 'player_props' || a.addonType === 'bundle');
    if (type === 'private') return sub.addons.some(a => a.addonType === 'private' || a.addonType === 'bundle');
    return sub.addons.some(a => a.addonType === type);
  },

  getActiveAddons: () => {
    const sub = get().currentSubscription;
    return sub?.addons || [];
  },

  hasTeamOutcomes: () => {
    const sub = get().currentSubscription;
    return sub?.features?.hasTeamOutcomes ?? false;
  },

  hasPrivateModels: () => {
    const sub = get().currentSubscription;
    return sub?.features?.hasPrivateModels ?? false;
  },

  // Trial helpers
  isInTrial: () => {
    const sub = get().currentSubscription;
    return !!sub && sub.status === 'trialing';
  },

  isTrialExpired: () => {
    const sub = get().currentSubscription;
    if (!sub || !sub.trialEndsAt) return false;
    // Trial is expired if trialEndsAt is in the past and status is not active
    return sub.trialEndsAt < Date.now() && sub.status !== 'active';
  },

  getTrialDaysRemaining: () => {
    const sub = get().currentSubscription;
    if (!sub || !sub.trialEndsAt) return 0;
    const now = Date.now();
    const diff = sub.trialEndsAt - now;
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24)); // Convert ms to days
  },

  getTrialUrgencyLevel: () => {
    const days = get().getTrialDaysRemaining();
    if (days <= 1) return 'urgent';      // 0-1 days: red/urgent
    if (days <= 7) return 'warning';     // 2-7 days: amber/warning
    return 'normal';                      // 8-14 days: blue/normal
  },

  // Utility
  setError: (error) => set({ error }),

  reset: () => set({
    currentSubscription: null,
    products: [],
    invoices: [],
    selectedInvoice: null,
    hasMoreInvoices: false,
    prorationPreview: null,
    isLoadingSubscription: false,
    isLoadingProducts: false,
    isLoadingInvoices: false,
    isChangingPlan: false,
    isCanceling: false,
    isReactivating: false,
    isCreatingSession: false,
    isAddingAddon: false,
    isRemovingAddon: false,
    error: null
  })
}));
